/**
 * Scoring a sung take against a song's reference contour.
 *
 * One module, used by both the singing room and the solo karaoke page, so a singer
 * cannot get 91 in one place and 68 in the other for the same performance. That was
 * an explicit requirement, and it is the reason this is not written inline in either
 * component.
 *
 * ── What is actually being measured ──────────────────────────────────────────
 * There is no MIDI or note chart for any of these songs. The reference is a pitch
 * contour recovered from the master minus the instrumental (see
 * `scripts/extract-contour.mjs`), so it is a per-frame f0 track, not a score. That
 * limits what can honestly be claimed: this measures whether the singer's pitch
 * moves with the original's pitch, whether they sing at the right moments, and
 * whether they sing at all. It is not a judgement of tone or musicality.
 *
 * ── Gaming ───────────────────────────────────────────────────────────────────
 * Two attacks have to fail, and they are tested in `scoring.test.mjs`:
 *   1. Silent mic. Handled by `completeness` plus a hard floor — if almost nothing
 *      is voiced there is nothing to score and the take returns 0, not a default.
 *   2. One constant tone held for the whole song. This is the dangerous one: on a
 *      pitch-difference metric alone a drone parked on the song's median note can
 *      score respectably. It is defeated by `contourFit`, a correlation between the
 *      singer's pitch movement and the reference's, applied as a MULTIPLIER rather
 *      than a weighted term — a drone has no movement to correlate, so it cannot
 *      buy the marks back by scoring well elsewhere.
 */
import { TRACK } from './pitchTrack.mjs';

/** Weighted components. Pitch dominates, but not so much that a singer who nails a
 *  few notes and skips the rest can outscore someone who sings the whole song. */
export const WEIGHTS = Object.freeze({ pitch: 0.5, timing: 0.2, completeness: 0.3 });

/** Cent tolerances: full marks inside PERFECT, nothing beyond ZERO, linear between.
 *  ±50 cents is a quarter tone — comfortably "in tune" to a listener. */
const CENTS_PERFECT = 50;
const CENTS_ZERO = 200;

/** Device latency to search over when aligning the take. The studio already has a
 *  ±400 ms sync slider for exactly this, so the same range is used here. */
const MAX_OFFSET_SEC = 0.4;

/** Below this voiced ratio there is no performance to score. */
const MIN_VOICED_RATIO = 0.05;

export const GRADE_BANDS = Object.freeze([
  { grade: 'A+', min: 90 },
  { grade: 'A', min: 75 },
  { grade: 'B', min: 60 },
  { grade: 'C', min: 0 },
]);

export function gradeFor(score) {
  return GRADE_BANDS.find((b) => score >= b.min).grade;
}

/** Per-frame pitch mark from a cents deviation. Exported so the live scorer cannot
 *  drift from the final one by re-deriving it. */
export function pitchAccuracy(cents) {
  return Math.max(0, Math.min(1,
    (CENTS_ZERO - Math.abs(cents)) / (CENTS_ZERO - CENTS_PERFECT)));
}

/**
 * Combine the three components into 0–100.
 *
 * The single source of truth for the formula. `runningScore.mjs` shows a number that
 * ticks upward while someone sings and `scoreTake` produces the one that gets
 * recorded; if those two ever disagreed the feature would look broken at exactly the
 * moment the singer is paying most attention. They call this.
 *
 * `fit` is applied as a MULTIPLIER, not a fourth weighted term: a held drone has no
 * pitch movement to correlate, and must not be able to buy the marks back by scoring
 * well on completeness. Floored at 0.35 so a flat-but-honest delivery still scores.
 */
export function combineScore({ pitch, timing, completeness, fit }) {
  const fitPenalty = 0.35 + 0.65 * Math.min(1, Math.max(0, fit) / 0.5);
  const raw = (WEIGHTS.pitch * pitch)
    + (WEIGHTS.timing * timing)
    + (WEIGHTS.completeness * completeness);
  return Math.round(Math.max(0, Math.min(100, raw * fitPenalty * 100)));
}

/** Pearson correlation over [singerMidi, refMidi] pairs, from running sums.
 *  Zero variance on either side yields 0 — which is what kills a drone. */
export function pearsonFromSums({ n, sx, sy, sxx, syy, sxy }) {
  if (n < 20) return 0;
  const cov = sxy - (sx * sy) / n;
  const vx = sxx - (sx * sx) / n;
  const vy = syy - (sy * sy) / n;
  if (vx < 1e-6 || vy < 1e-6) return 0;
  return cov / Math.sqrt(vx * vy);
}

/** Unpack the wire format written by the extractor (0 = unvoiced, else MIDI × 10). */
export function decodeContour(contour) {
  return {
    ...contour,
    frames: contour.midi.map((v, i) => ({
      t: i * contour.hopSec,
      midi: v === 0 ? null : v / 10,
      energy: contour.energy ? contour.energy[i] / 100 : 0,
    })),
  };
}

/**
 * Signed distance between two notes ignoring octave, in cents.
 *
 * Octave-insensitive because most of this site sings in a different octave to the
 * Suno vocal, and punishing that would make the score measure vocal range rather
 * than accuracy. Wrapped to ±6 semitones so a semitone-flat note reads as -100
 * cents, not +1100.
 */
export function centsApart(singerMidi, refMidi) {
  let d = (singerMidi - refMidi) % 12;
  if (d > 6) d -= 12;
  if (d < -6) d += 12;
  return d * 100;
}

function frameAt(frames, t, hopSec) {
  const i = Math.round(t / hopSec);
  return i >= 0 && i < frames.length ? frames[i] : null;
}

/**
 * Find the systematic offset between singer and reference by correlating their
 * voiced/unvoiced masks.
 *
 * This is device latency (output latency + mic path), not the singer's timing, and
 * removing it first is what makes the remaining timing number mean something. It is
 * reported separately so the UI can say "your mic is 120 ms late" instead of
 * silently marking someone down for their hardware.
 */
export function estimateOffsetSec(refFrames, singerFrames, hopSec) {
  const maxLag = Math.round(MAX_OFFSET_SEC / hopSec);
  let bestLag = 0;
  let bestScore = -1;
  for (let lag = -maxLag; lag <= maxLag; lag += 1) {
    let agree = 0;
    let n = 0;
    for (let i = 0; i < refFrames.length; i += 1) {
      const s = singerFrames[i + lag];
      if (!s) continue;
      const refVoiced = refFrames[i].midi != null;
      const sngVoiced = s.midi != null;
      if (refVoiced || sngVoiced) { n += 1; if (refVoiced && sngVoiced) agree += 1; }
    }
    const score = n > 0 ? agree / n : 0;
    if (score > bestScore) { bestScore = score; bestLag = lag; }
  }
  return bestLag * hopSec;
}

/** Pearson correlation of the two pitch tracks over frames where both are voiced.
 *  Zero variance on either side yields 0 — which is precisely what kills a drone. */
function contourFit(pairs) {
  if (pairs.length < 20) return 0;
  let sx = 0; let sy = 0;
  for (const [x, y] of pairs) { sx += x; sy += y; }
  const mx = sx / pairs.length;
  const my = sy / pairs.length;
  let num = 0; let dx = 0; let dy = 0;
  for (const [x, y] of pairs) {
    num += (x - mx) * (y - my);
    dx += (x - mx) ** 2;
    dy += (y - my) ** 2;
  }
  if (dx < 1e-6 || dy < 1e-6) return 0;
  return num / Math.sqrt(dx * dy);
}

/**
 * Score a complete take.
 *
 * @param contour  the song's reference, as written by scripts/extract-contour.mjs
 * @param singerFrames  output of trackFrames() on the singer's mic at the same
 *                      sample rate, frame and hop (guaranteed by pitchTrack.mjs)
 * @param startSec  where in the song the take began, if not 0
 */
export function scoreTake(contour, singerFrames, { startSec = 0 } = {}) {
  const ref = decodeContour(contour);
  const hopSec = contour.hopSec ?? TRACK.hop / TRACK.sampleRate;

  // Align the singer onto the reference timeline before anything else.
  const shifted = singerFrames.map((f) => ({ ...f, t: f.t + startSec }));
  const indexed = [];
  for (const f of shifted) indexed[Math.round(f.t / hopSec)] = f;

  const offsetSec = estimateOffsetSec(ref.frames, indexed, hopSec);

  const singerVoiced = singerFrames.filter((f) => f.midi != null).length;
  const voicedRatio = singerFrames.length ? singerVoiced / singerFrames.length : 0;

  // The part of the song the take actually covered — a 30s attempt at a 250s song
  // is judged on those 30s for pitch, but `completeness` still sees the whole song.
  const firstIdx = Math.round(startSec / hopSec);
  const lastIdx = Math.min(ref.frames.length - 1, firstIdx + singerFrames.length);

  let pitchSum = 0;
  let pitchN = 0;
  let sungWhenExpected = 0;
  let expected = 0;
  const pairs = [];
  const deviations = [];

  for (let i = firstIdx; i <= lastIdx; i += 1) {
    const r = ref.frames[i];
    if (!r) continue;
    const s = frameAt(indexed, i * hopSec + offsetSec, hopSec);
    if (r.midi != null) {
      expected += 1;
      if (s && s.midi != null) {
        sungWhenExpected += 1;
        const cents = centsApart(s.midi, r.midi);
        const acc = pitchAccuracy(cents);
        pitchSum += acc;
        pitchN += 1;
        pairs.push([s.midi, r.midi]);
        deviations.push(cents);
      }
    }
  }

  // Nothing sung: return zero explicitly rather than letting empty averages
  // produce a flattering NaN-turned-default.
  if (voicedRatio < MIN_VOICED_RATIO || expected === 0 || pitchN < 20) {
    return {
      score: 0,
      grade: 'C',
      pitch: 0,
      timing: 0,
      completeness: 0,
      contourFit: 0,
      offsetMs: 0,
      voicedRatio: Number(voicedRatio.toFixed(3)),
      reason: voicedRatio < MIN_VOICED_RATIO
        ? 'No singing detected — check the microphone.'
        : 'Too little of the song was sung to score.',
    };
  }

  const pitch = pitchSum / pitchN;
  const completeness = sungWhenExpected / expected;

  // Timing: how tightly the singer's onsets sit around the (already offset-corrected)
  // reference. Spread, not mean — the mean is device latency and has been removed.
  const meanDev = deviations.reduce((a, b) => a + b, 0) / deviations.length;
  const spread = Math.sqrt(
    deviations.reduce((a, c) => a + (c - meanDev) ** 2, 0) / deviations.length,
  );
  const timingFromMask = maskAgreement(ref.frames, indexed, hopSec, offsetSec, firstIdx, lastIdx);
  const timing = Math.max(0, Math.min(1, timingFromMask * (1 - Math.min(1, spread / 600))));

  const fit = Math.max(0, contourFit(pairs));
  const score = combineScore({ pitch, timing, completeness, fit });

  return {
    score,
    grade: gradeFor(score),
    pitch: Number(pitch.toFixed(3)),
    timing: Number(timing.toFixed(3)),
    completeness: Number(completeness.toFixed(3)),
    contourFit: Number(fit.toFixed(3)),
    offsetMs: Math.round(offsetSec * 1000),
    voicedRatio: Number(voicedRatio.toFixed(3)),
    reason: null,
  };
}

function maskAgreement(refFrames, indexed, hopSec, offsetSec, firstIdx, lastIdx) {
  let agree = 0;
  let n = 0;
  for (let i = firstIdx; i <= lastIdx; i += 1) {
    const r = refFrames[i];
    if (!r) continue;
    const s = frameAt(indexed, i * hopSec + offsetSec, hopSec);
    const rv = r.midi != null;
    const sv = !!(s && s.midi != null);
    if (rv || sv) { n += 1; if (rv === sv) agree += 1; }
  }
  return n > 0 ? agree / n : 0;
}

/**
 * Rolling meter for the live "are they on the beat" wave.
 *
 * Deliberately cheap: it is called on every animation frame while someone is singing,
 * on phones, in a room that is also holding a WebRTC mesh open. It keeps a short
 * ring of recent frames and reports the current pitch error and whether the singer
 * is running ahead of or behind the reference.
 */
export class LiveMeter {
  constructor(contour, { windowSec = 3 } = {}) {
    this.ref = decodeContour(contour);
    this.hopSec = contour.hopSec ?? TRACK.hop / TRACK.sampleRate;
    this.windowFrames = Math.round(windowSec / this.hopSec);
    this.recent = [];
    this.lastLag = null;
  }

  /** Push one tracked frame from the singer. `t` is seconds into the song. */
  push(frame) {
    this.recent.push(frame);
    if (this.recent.length > this.windowFrames) this.recent.shift();
  }

  /**
   * @returns {{cents:number|null, drift:number|null, voiced:boolean,
   *            refMidi:number|null, singerMidi:number|null}}
   *   `drift` is milliseconds: negative = singing early, positive = late.
   */
  read(tSec) {
    const i = Math.round(tSec / this.hopSec);
    const r = this.ref.frames[i] || null;
    const newest = this.recent[this.recent.length - 1] || null;

    // Report the most recent frame that actually carried a pitch, not simply the
    // newest one. Singing is mostly gaps — consonants, breaths, rests — so keying
    // the "in tune" readout to the latest frame leaves it blank most of the time,
    // which reads as a broken meter rather than as "you are between notes".
    let s = null;
    const lookback = Math.min(this.recent.length, Math.round(0.6 / this.hopSec));
    for (let k = this.recent.length - 1; k >= this.recent.length - lookback; k -= 1) {
      if (this.recent[k] && this.recent[k].midi != null) { s = this.recent[k]; break; }
    }

    const out = {
      voiced: !!(newest && newest.midi != null),
      refMidi: r ? r.midi : null,
      singerMidi: s ? s.midi : null,
      cents: null,
      drift: null,
    };

    // Compare against the reference at the moment that frame was sung, not at
    // "now" — up to 0.6s may have passed and the reference may have moved on.
    if (s) {
      const at = s.t != null ? Math.round(s.t / this.hopSec) : i;
      const rs = this.ref.frames[at];
      if (rs && rs.midi != null) out.cents = centsApart(s.midi, rs.midi);
    }

    // Drift: which lag lines the singer's voiced mask up best with the reference's
    // over the recent window.
    if (this.recent.length >= this.windowFrames / 2) {
      const maxLag = Math.round(0.3 / this.hopSec);
      const scoreLag = (lag) => {
        let agree = 0;
        let n = 0;
        for (let k = 0; k < this.recent.length; k += 1) {
          const rr = this.ref.frames[i - this.recent.length + k + lag];
          if (!rr) continue;
          const rv = rr.midi != null;
          const sv = this.recent[k].midi != null;
          if (rv || sv) { n += 1; if (rv === sv) agree += 1; }
        }
        return n > 0 ? agree / n : 0;
      };

      let bestLag = 0;
      let best = -1;
      for (let lag = -maxLag; lag <= maxLag; lag += 1) {
        // These songs are repetitive, so a phrase often matches itself a few
        // hundred ms away just as well as it matches in place. Left alone the
        // readout snapped between 0 and ~290ms every couple of seconds and looked
        // broken. Bias toward no drift so an alternative alignment has to be
        // genuinely better, not merely equal, to win.
        const sc = scoreLag(lag) - Math.abs(lag) * 0.004;
        if (sc > best) { best = sc; bestLag = lag; }
      }

      // Hysteresis: keep the previous answer unless the new one clearly beats it.
      // Without this the value still flickers between two near-equal alignments.
      if (this.lastLag != null && bestLag !== this.lastLag) {
        const keep = scoreLag(this.lastLag) - Math.abs(this.lastLag) * 0.004;
        if (best - keep < 0.06) bestLag = this.lastLag;
      }
      this.lastLag = bestLag;
      out.drift = Math.round(bestLag * this.hopSec * 1000);
    }
    return out;
  }
}
